/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {calculateNetPrice} from './utils'

/**
 * Get the human-friendly version of the variation values that users have selected.
 * Useful for displaying these values in the UI.
 *
 * @param {Object} variationAttributes - The products variation attributes.
 * @param {Object} values - The variations selected attribute values.
 * @returns {Object} - A key value map of the display name and display value.
 *
 * @example
 * const displayValues = getDisplayVariationValues(
 *     [ { "id": "color", "name": "Colour", "values": [ { "name": "royal", "orderable": true, "value": "JJ5FUXX" } ] } ],
 *     { "color": "JJ5FUXX" }
 * )
 * // returns { "Colour": "royal" }
 */
export const getDisplayVariationValues = (variationAttributes, values = {}) => {
    const returnVal = Object.entries(values).reduce((acc, [id, value]) => {
        const attribute = variationAttributes.find(({id: attributeId}) => attributeId === id)
        const attributeValue = attribute.values.find(
            ({value: attributeValue}) => attributeValue === value
        )
        return {
            ...acc,
            [attribute.name]: attributeValue.name
        }
    }, {})
    return returnVal
}

/**
 * Used to return the preferred variant if orderable or first orderable or if there is no orderable variants - first variant
 *
 * @param {Object} variants - The products variants.
 * @param {Boolean} c_selectMostSoldVariant - The products c_selectMostSoldVariant.
 * @param {String} c_preferedVariantId - The products c_preferedVariantId.
 * @returns {Object} - The preselected variant.
 */
export const getPreferedVariant = ({variants, c_preferedVariantId}) => {
    // If there is a preferred variant and it is orderable, return it
    if (c_preferedVariantId) {
        const preferedVariant = variants?.find(
            (variant) => variant.productId === c_preferedVariantId
        )
        if (preferedVariant?.orderable) {
            return preferedVariant
        }
    }

    // If there is an orderable variant return the first one
    const firstOrderableVariant = variants?.find((variant) => variant.orderable === true)
    if (firstOrderableVariant) {
        return firstOrderableVariant
    }

    // Return the first variant
    return variants?.[0]
}
/**
 * Returns the master product id of the given variant product if available
 * @param {object} product
 * @return {string|null} master product id or null
 */
export const getMasterProductId = (product) =>
    product?.type?.variant ? product.master?.masterId : null
/**
 * Returns the product ids used to search for BazaarVoice reviews
 * @param {object} product
 * @return {string|null} product ids used to search for BazaarVoice reviews
 */
export const getBVReviewProductIds = (product) =>
    getMasterProductId(product) || product?.id || product?.c_master || product?.productId
/**
 * Returns the basket items for the given product and engrave text
 * @param {object} product
 * @param {string} [engraveText]
 * @param {number} [productQuantity]
 * @returns {[{quantity: number, productId: string, c_engraving?: string}]}
 */
export const getProductAddToBasketItems = (product, engraveText, productQuantity) => {
    let engraved = !engraveText
    return getProductsToAddToCart(product).map((productSetItem) => {
        const productId = productSetItem?.productId || productSetItem?.id
        const quantity =
            productQuantity || productSetItem?.quantity || productSetItem?.minOrderQuantity || 1

        if (
            !engraved &&
            isProductEngravable(productSetItem) &&
            // only the root product or the set product itself can be engraved - extenstions and accessories can't be engraved
            (product === productSetItem || product.setProducts?.includes(productSetItem))
        ) {
            engraved = true
            return {
                productId,
                quantity,
                c_engraving: engraveText
            }
        } else {
            return {
                productId,
                quantity
            }
        }
    })
}

/**
 * Get the product display and discount prices
 *
 * @param {Object} product - The product to get the prices for.
 * @param {Boolean} isBonusProduct - True if the product is a bonus product (e.g. price 0), false otherwise.
 * @param {boolean} isD2BEnabled
 * @return {{isFree: boolean, displayPrice: number, price: number, hasDiscount: boolean, promotionalPrice: (number|undefined)}}
 */

export const getDisplayPrice = (product, isBonusProduct, isD2BEnabled) => {
    let price = Math.max(product?.price, 0) || 0
    const promotion = product?.productPromotions
        ?.filter((promotion) => promotion.promotionalPrice != null)
        .reduce(
            (prev, current) =>
                prev && prev.promotionalPrice < current.promotionalPrice ? prev : current,
            null
        )
    let promotionalPrice = isBonusProduct
        ? 0
        : promotion?.promotionalPrice != null
        ? Math.max(promotion?.promotionalPrice, 0)
        : null

    let displayPrice = promotionalPrice != null ? Math.min(price, promotionalPrice) : price

    if (isD2BEnabled) {
        price = calculateNetPrice(price, product?.c_taxRate)
        promotionalPrice = calculateNetPrice(promotionalPrice, product?.c_taxRate)
        displayPrice = calculateNetPrice(displayPrice, product.c_taxRate)
    }

    const hasDiscount = displayPrice !== price

    return {
        price,
        promotionalPrice: hasDiscount ? promotionalPrice : undefined,
        displayPrice,
        hasDiscount,
        isFree: displayPrice === 0
    }
}
/**
 * True if the given product can be engraved, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductEngravable = (product) =>
    Boolean(
        isProductSet(product)
            ? product.setProducts?.some((setPr) => isProductEngravable(setPr))
            : isProductOrderable(product) && product?.c_isEngravable
    )
/**
 * True if the given product is a master product, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductMaster = (product) => Boolean(product?.type?.master)
/**
 * True if the given product is a variant product, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductVariant = (product) => Boolean(product?.type?.variant)
/**
 * True if the given product can be ordered, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductOrderable = (product) =>
    Boolean(
        isProductSet(product)
            ? product.setProducts?.some((p) => isProductOrderable(p))
            : product?.c_isOrderable || product?.inventory?.orderable || product?.orderable
    )

/**
 * True if the given product is a product set, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductSet = (product) =>
    Boolean(product?.type?.set) || Boolean(product?.productType?.set)

/**
 * True if the given product is a customizable product set, false otherwise
 * @param {object} product
 * @return {boolean}
 */
export const isCustomizableProductSet = (product) =>
    isProductSet(product) && Boolean(product?.c_extendableSet)

/**
 * Return the products that will be added to the basket
 * @param {Object} product - The product to get the products for.
 * @return {Array<Object>} - The products that will be added to the basket.
 */
export const getProductsToAddToCart = (product) => {
    if (!isProductSet(product)) {
        return isProductOrderable(product) ? [product] : []
    }

    return [
        ...product.setProducts,
        ...[...(product.setExtensions || []), ...(product.setAccessories || [])].filter(
            (setProduct) => setProduct.isSetProductSelected
        )
    ].flatMap((p) => getProductsToAddToCart(p))
}

/**
 * Filter product's variation attribute values based on the filter function
 * @param {Object} variationAttributes - The products variation attributes.
 * @param {function (variationValue: Object, variationAttribute: Object): boolean} filter - The filter function to apply to the variation values.
 * @return {Object} - The filtered variation attributes.
 */
export const filterVariationAttributeValues = (variationAttributes, filter) =>
    variationAttributes.map((vars) => ({
        ...vars,
        values: vars.values.filter((va) => filter(va, vars))
    }))

/**
 * Returns a filter function that only returns the selected variation values
 * @param {Object} variationValues - The selected variation values.
 * @return {function(Object, Object): boolean} - The filter function.
 */
export const onlySelectedVariationValues = (variationValues) => (va, vars) =>
    va.value === variationValues[vars.id]
